
import React, { useState } from 'react';
import { BarChart3, Target, Users, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';
import tni1 from "../assets/images/Tni1.png";
import tni2 from "../assets/images/Tni2.jpeg";
import tni3 from "../assets/images/Tni3.jpeg";
import tni4 from "../assets/images/Tni4.png";
import heroBg from "../assets/images/carousel_6.jpg";
import Layout from '../component/layout/Layout';
import ConsultationModal from '../component/ConsultationModal';

export default function TniPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const features = [
    'Business-centric Analysis',
    'Industry-aligned Framework',
    'Structured Methodology',
    'Custom Training Plans',
    'Measurable Outcomes'
  ];

  const WaveDivider = () => (
    <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', overflow: 'hidden', lineHeight: 0, zIndex: 3 }}>
      <svg viewBox="0 0 1440 320" preserveAspectRatio="none" style={{ position: 'relative', display: 'block', width: 'calc(100% + 1.3px)', height: '100px' }}>
        <path fill="#ffffff" fillOpacity="1" d="M0,192L48,197.3C96,203,192,213,288,197.3C384,181,480,139,576,138.7C672,139,768,181,864,208C960,235,1056,245,1152,234.7C1248,224,1344,192,1392,176L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
      </svg>
    </div>
  );

  return (
    <Layout>
      <div className="tni-page">
        <style>{`
        .tni-page {
          min-height: 100vh;
          background: linear-gradient(to bottom, #f8fafc, #ffffff);
        }

        /* Hero Section */
        .hero-section {
          position: relative;
          min-height: 65vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background-image: url(${heroBg});
          background-size: cover;
          background-position: center;
          background-attachment: fixed;
          padding: 80px 20px 100px;
          overflow: visible;
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(10, 35, 80, 0.6) 0%, rgba(8, 25, 55, 0.65) 100%);
          z-index: 1;
        }

        .hero-container {
          max-width: 1200px;
          margin: 0 auto;
          width: 100%;
          position: relative;
          z-index: 2;
        }

        .hero-content {
          position: relative;
          z-index: 2;
          max-width: 900px;
          text-align: center;
          color: #ffffff;
          padding: 0 16px;
          margin: 0 auto;
        }

        .hero-title {
          font-size: 3.5rem;
          font-weight: 900;
          margin-bottom: 20px;
          line-height: 1.15;
          color: #ffffff;
          text-shadow: 0 8px 24px rgba(0,0,0,0.6);
          letter-spacing: -0.5px;
        }

        .hero-subtitle {
          font-size: 1.15rem;
          color: #e8f0ff;
          margin-bottom: 40px;
          line-height: 1.65;
          max-width: 780px;
          margin-left: auto;
          margin-right: auto;
          font-weight: 400;
        }

        .hero-buttons {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 16px;
          align-items: center;
        }

        .btn-primary {
          background: #ffffff;
          color: #0a1a4d;
          padding: 14px 36px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 1rem;
          border: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 10px 28px rgba(0, 0, 0, 0.25);
        }

        .btn-primary:hover {
          transform: translateY(-3px);
          background: #f5f8ff;
          box-shadow: 0 14px 35px rgba(0, 0, 0, 0.35);
        }

        .btn-secondary {
          border: 2.5px solid rgba(255,255,255,0.95);
          background: rgba(255,255,255,0.08);
          color: #ffffff;
          padding: 14px 36px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(10px);
        }

        .btn-secondary:hover {
          background: rgba(255,255,255,0.2);
          border-color: #ffffff;
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }

        /* Section Styles */
        .section {
          padding: 64px 16px;
        }

        .section-white {
          background: white;
        }

        .section-gray {
          background: #f8fafc;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .section-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .section-title {
          font-size: 2.5rem;
          font-weight: bold;
          color: #0f172a;
          margin-bottom: 16px;
        }

        .section-subtitle {
          font-size: 1.25rem;
          color: #64748b;
          max-width: 768px;
          margin: 0 auto;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .stat-card {
          padding: 24px;
          border-radius: 12px;
          border: 1px solid;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          min-height: 240px;
        }

        .stat-card-blue {
          background: linear-gradient(135deg, #dbeafe, #bfdbfe);
          border-color: #93c5fd;
        }

        .stat-card-orange {
          background: linear-gradient(135deg, #fed7aa, #fdba74);
          border-color: #fb923c;
        }

        .stat-card-red {
          background: linear-gradient(135deg, #fecaca, #fca5a5);
          border-color: #f87171;
        }

        .stat-card-green {
          background: linear-gradient(135deg, #bbf7d0, #86efac);
          border-color: #4ade80;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: bold;
          margin-bottom: 8px;
        }

        .stat-card-blue .stat-number { color: #1e3a8a; }
        .stat-card-orange .stat-number { color: #7c2d12; }
        .stat-card-red .stat-number { color: #7f1d1d; }
        .stat-card-green .stat-number { color: #14532d; }

        .stat-text {
          color: #334155;
          font-size: 0.875rem;
          margin-bottom: 8px;
        }

        .stat-source {
          font-size: 0.75rem;
          color: #64748b;
          margin-bottom: 12px;
        }

        .view-source-btn {
          background: #e2e8f0;
          color: #1e3a8a;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s, color 0.3s;
          display: inline-block;
          text-decoration: none;
        }

        .view-source-btn:hover {
          background: #cbd5e1;
          color: #0f172a;
        }

        .view-source-btn {
          margin-top: auto;
        }

        .conclusion-box {
          background: #0f172a;
          color: white;
          padding: 24px;
          border-radius: 12px;
          text-align: center;
        }

        .conclusion-text {
          font-size: 1.25rem;
          font-weight: 600;
        }

        /* Image Section */
        .image-wrapper {
          background: white;
          padding: 16px;
          border-radius: 12px;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          margin-bottom: 24px;
        }

        .image-wrapper img {
          width: 100%;
          height: auto;
          border-radius: 8px;
          display: block;
        }

        /* Approach Grid */
        .approach-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 24px;
          margin-bottom: 48px;
        }

        .approach-item {
          text-align: center;
        }

        .approach-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
        }

        .icon-blue { background: #dbeafe; color: #2563eb; }
        .icon-orange { background: #fed7aa; color: #ea580c; }
        .icon-green { background: #bbf7d0; color: #16a34a; }
        .icon-purple { background: #e9d5ff; color: #9333ea; }

        .approach-title {
          font-weight: 600;
          font-size: 1.125rem;
          margin-bottom: 8px;
          color: #0f172a;
        }

        .approach-text {
          color: #64748b;
          font-size: 0.875rem;
        }

        .strategy-box {
          background: #1e3a8a;
          color: white;
          padding: 32px;
          border-radius: 12px;
          text-align: center;
        }

        .strategy-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .strategy-subtitle {
          font-size: 1.25rem;
          color: #bfdbfe;
        }

        /* 5-Step Model */
        .steps-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }

        .step-card {
          background: white;
          padding: 24px;
          border-radius: 12px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          transition: box-shadow 0.3s;
        }

        .step-card:hover {
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .step-number {
          width: 48px;
          height: 48px;
          background: #2563eb;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: bold;
          margin-bottom: 16px;
        }

        .step-title {
          font-weight: bold;
          font-size: 1.125rem;
          margin-bottom: 8px;
          color: #0f172a;
        }

        .step-desc {
          color: #64748b;
          font-size: 0.875rem;
        }

        /* Table Styles */
        .table-wrapper {
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .table-scroll {
          overflow-x: auto;
        }

        .training-table {
          width: 100%;
          border-collapse: collapse;
        }

        .training-table thead {
          background: #1e3a8a;
          color: white;
        }

        .training-table th {
          padding: 16px 24px;
          text-align: left;
          font-size: 1.125rem;
          font-weight: 600;
        }

        .training-table th.center {
          text-align: center;
        }

        .training-table tbody tr {
          border-bottom: 1px solid #e2e8f0;
          transition: background 0.3s;
        }

        .training-table tbody tr:hover {
          background: #f8fafc;
        }

        .training-table td {
          padding: 16px 24px;
        }

        .training-table td.center {
          text-align: center;
        }

        .topic-name {
          font-weight: 600;
          color: #0f172a;
        }

        .percentage-badge {
          display: inline-block;
          padding: 4px 16px;
          border-radius: 9999px;
          font-weight: 600;
        }

        .badge-blue {
          background: #dbeafe;
          color: #1e3a8a;
        }

        .badge-orange {
          background: #fed7aa;
          color: #7c2d12;
        }

        .badge-green {
          background: #bbf7d0;
          color: #14532d;
        }

        .table-footer {
          background: #f8fafc;
          padding: 16px 24px;
          border-top: 1px solid #e2e8f0;
        }

        .table-footer-text {
          font-size: 0.875rem;
          color: #64748b;
        }

        .table-footer-text strong {
          font-weight: 600;
        }

        /* Model Info Box */
        .model-info-box {
          background: #dbeafe;
          border-left: 4px solid #2563eb;
          padding: 24px;
          border-radius: 0 8px 8px 0;
          margin-top: 24px;
        }

        .model-info-title {
          color: #334155;
          font-weight: 500;
          margin-bottom: 8px;
        }

        .model-info-title strong {
          font-weight: bold;
        }

        .model-info-text {
          color: #64748b;
        }

        /* Benefits Grid */
        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 24px;
        }

        .benefit-card {
          background: white;
          padding: 24px;
          border-radius: 12px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          transition: box-shadow 0.3s;
        }

        .benefit-card:hover {
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .benefit-icon {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
        }

        .benefit-title {
          font-weight: 600;
          font-size: 1.125rem;
          color: #0f172a;
        }

        /* Why SkillServe Section */
        .why-section {
          background: linear-gradient(135deg, #1e3a8a, #1e40af);
          color: white;
          padding: 64px 16px;
        }

        .why-title {
          font-size: 2.5rem;
          font-weight: bold;
          margin-bottom: 16px;
        }

        .why-subtitle {
          font-size: 1.5rem;
          color: #bfdbfe;
          font-weight: 600;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 24px;
          margin-top: 48px;
        }

        .feature-item {
          text-align: center;
        }

        .feature-icon {
          margin: 0 auto 12px;
          color: #4ade80;
        }

        .feature-text {
          font-weight: 600;
        }

        /* CTA Section */
        .cta-section {
          padding: 80px 16px;
          background: white;
          text-align: center;
        }

        .cta-container {
          max-width: 896px;
          margin: 0 auto;
        }

        .cta-title {
          font-size: 2.5rem;
          font-weight: bold;
          color: #0f172a;
          margin-bottom: 24px;
        }

        .cta-subtitle {
          font-size: 1.5rem;
          color: #64748b;
          margin-bottom: 32px;
        }

        .cta-buttons {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 48px;
        }

        .btn-cta-primary {
          background: #1e3a8a;
          color: white;
          padding: 16px 32px;
          border-radius: 8px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: background 0.3s;
        }

        .btn-cta-primary:hover {
          background: #1e40af;
        }

        .btn-cta-secondary {
          border: 2px solid #1e3a8a;
          background: transparent;
          color: #1e3a8a;
          padding: 16px 32px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s;
        }

        .btn-cta-secondary:hover {
          background: #dbeafe;
        }

        /* Footer */
        .footer {
          background: #0f172a;
          color: #94a3b8;
          padding: 32px 16px;
        }

        .footer-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .footer-title {
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: 12px;
          color: #cbd5e1;
        }

        .footer-list {
          font-size: 0.75rem;
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .footer-list li {
          margin-bottom: 4px;
        }

        /* Responsive */
        @media (min-width: 640px) {
          .cta-buttons {
            flex-direction: row;
            justify-content: center;
          }
        }

        @media (min-width: 768px) {
          .hero-title {
            font-size: 3.8rem;
          }

          .hero-subtitle {
            font-size: 1.3rem;
          }

          .section-title {
            font-size: 2.5rem;
          }

          .cta-title {
            font-size: 3rem;
          }
        }

        @media (max-width: 767px) {
          .hero-section {
            min-height: 58vh;
            padding: 60px 16px 80px;
          }

          .hero-title {
            font-size: 2.2rem;
          }

          .hero-subtitle {
            font-size: 1rem;
            margin-bottom: 28px;
          }

          .hero-buttons {
            gap: 12px;
          }

          .btn-primary,
          .btn-secondary {
            padding: 12px 24px;
            font-size: 0.95rem;
          }

          .section-title {
            font-size: 1.875rem;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .approach-grid,
          .steps-grid,
          .benefits-grid,
          .features-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-overlay" />
          <div className="hero-container">
            <div className="hero-content">
              <h1 className="hero-title">
                Turn Skill Gaps into Business Advantage
              </h1>
              <p className="hero-subtitle">
                Training Need Identification (TNI) - A structured method to identify skill gaps and deliver training that improves performance and business outcomes.
              </p>
              <div className="hero-buttons">
                <button
                  className="btn-primary"
                  onClick={() => {
                    // navigate to contact or open modal
                    setIsModalOpen(true);
                  }}
                >
                  Request Skill Assessment <ArrowRight size={20} />
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => {
                    if (typeof window !== 'undefined') window.location.href = '/contact-us';
                  }}
                >
                  Get Gap Report
                </button>
              </div>
            </div>
          </div>
          <WaveDivider />
        </section>

        {/* Why TNI Matters */}
        <section className="section section-white">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">
                Why Training Need Identification Matters
              </h2>
              <p className="section-subtitle">
                Industries today are facing rapid change due to automation, technology and shifting job roles. Training without understanding real skill gaps leads to wasted budget and poor performance.
              </p>
            </div>

            {/* Stats Grid */}
            <div className="stats-grid">
              <div className="stat-card stat-card-blue">
                <div className="stat-number">39%</div>
                <p className="stat-text">of core job skills will change by 2030 due to technological and economic shifts</p>
                <p className="stat-source">World Economic Forum – Future of Jobs Report 2025</p>
                <button
                  className="view-source-btn"
                  onClick={() => window.open('https://www.weforum.org/publications/the-future-of-jobs-report-2025/', '_blank', 'noopener,noreferrer')}
                >
                  View Source 🔗
                </button>
              </div>
              <div className="stat-card stat-card-orange">
                <div className="stat-number">92M / 170M</div>
                <p className="stat-text">jobs displaced vs. new opportunities created by 2030</p>
                <p className="stat-source">World Economic Forum – Press Release 2025</p>
                <button
                  className="view-source-btn"
                  onClick={() => window.open('https://www.weforum.org/press/2025/01/future-of-jobs-report-2025-78-million-new-job-opportunities-by-2030-but-urgent-upskilling-needed-to-prepare-workforces/', '_blank', 'noopener,noreferrer')}
                >
                  View Source 🔗
                </button>
              </div>
              <div className="stat-card stat-card-red">
                <div className="stat-number">$8.5T</div>
                <p className="stat-text">in unrealized revenue at risk by 2030 from talent shortages</p>
                <p className="stat-source">Korn Ferry – The $8.5 Trillion Talent Shortage</p>
                <button
                  className="view-source-btn"
                  onClick={() => window.open('https://www.kornferry.com/insights/this-week-in-leadership/the-8-5-trillion-talent-shortage', '_blank', 'noopener,noreferrer')}
                >
                  View Source 🔗
                </button>
              </div>
              <div className="stat-card stat-card-green">
                <div className="stat-number">94%</div>
                <p className="stat-text">of employees stay longer when companies actively support learning</p>
                <p className="stat-source">LinkedIn Workplace Learning Report</p>
                <button
                  className="view-source-btn"
                  onClick={() => window.open('https://learning.linkedin.com/resources/career-development/develop-employees', '_blank', 'noopener,noreferrer')}
                >
                  View Source 🔗
                </button>
              </div>
            </div>

            <div className="conclusion-box">
              <p className="conclusion-text">
                Skill gaps are now a direct business risk. TNI is no longer optional — it is essential.
              </p>
            </div>
          </div>
        </section>

        {/* Main Image Section 1 */}
        {/* <section className="section section-gray">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">
                Industry Transformation with TNI
              </h2>
              <p className="section-subtitle">Understanding the needs and benefits</p>
            </div>
            <div className="image-wrapper">
              <img
                src={tni1}
                alt="Industry transformation with TNI"
                loading="lazy"
              />
            </div>
          </div>
        </section> */}

        {/* Our Approach */}
        <section className="section section-white">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">
                Our Data-Driven Approach
              </h2>
              <p className="section-subtitle">At SkillServe, we use a structured methodology to deliver results</p>
            </div>

            <div className="approach-grid">
              <div className="approach-item">
                <div className="approach-icon icon-blue">
                  <BarChart3 size={32} />
                </div>
                <h3 className="approach-title">Analyse</h3>
                <p className="approach-text">Current workforce skills assessment</p>
              </div>
              <div className="approach-item">
                <div className="approach-icon icon-orange">
                  <Target size={32} />
                </div>
                <h3 className="approach-title">Identify</h3>
                <p className="approach-text">Capability gaps detection</p>
              </div>
              <div className="approach-item">
                <div className="approach-icon icon-green">
                  <Users size={32} />
                </div>
                <h3 className="approach-title">Design</h3>
                <p className="approach-text">Role-based training programs</p>
              </div>
              <div className="approach-item">
                <div className="approach-icon icon-purple">
                  <TrendingUp size={32} />
                </div>
                <h3 className="approach-title">Build</h3>
                <p className="approach-text">Long-term training roadmap</p>
              </div>
            </div>

            <div className="strategy-box">
              <p className="strategy-title">Training without diagnosis is waste.</p>
              <p className="strategy-subtitle">TNI turns training into strategy.</p>
            </div>
          </div>
        </section>

        {/* 5-Step Model */}
        <section className="section section-gray">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">
                Our 5-Step TNI Model
              </h2>
            </div>

            <div className="steps-grid">
              {[
                { num: '1', title: 'Business Understanding', desc: 'Goals, bottlenecks & risks' },
                { num: '2', title: 'Skill Mapping', desc: 'Audits, surveys and observation' },
                { num: '3', title: 'Gap Analysis', desc: 'Current vs required competency' },
                { num: '4', title: 'Training Design', desc: 'Role-based learning paths' },
                { num: '5', title: 'Review & Feedback', desc: 'Impact tracking and improvement' }
              ].map((step, i) => (
                <div key={i} className="step-card">
                  <div className="step-number">{step.num}</div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-desc">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Image Section 2 */}
        <section className="section section-white">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">
                Training Implementation Lifecycle
              </h2>
              <p className="section-subtitle">Complete lifecycle and implementation steps</p>
            </div>
            <div className="image-wrapper">
              <img
                src={tni2}
                alt="Training implementation lifecycle"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* Role-Based Training */}
        <section className="section section-gray">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">
                Role-Based Training Allocation
              </h2>
              <p className="section-subtitle">Data-driven training distribution across organizational levels</p>
            </div>

            <div className="table-wrapper">
              <div className="table-scroll">
                <table className="training-table">
                  <thead>
                    <tr>
                      <th>Topic</th>
                      <th className="center">Workers (L1)</th>
                      <th className="center">Supervisors (L2)</th>
                      <th className="center">Managers (L3)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { topic: '5S', l1: '80%', l2: '30%', l3: '5%' },
                      { topic: 'Kaizen', l1: '50%', l2: '20%', l3: '45%' },
                      { topic: 'MP Sheets', l1: '25%', l2: '35%', l3: '45%' },
                      { topic: 'Root Cause', l1: '10%', l2: '5%', l3: '60%' },
                      { topic: 'Lean', l1: '10%', l2: '15%', l3: '60%' }
                    ].map((row, i) => (
                      <tr key={i}>
                        <td className="topic-name">{row.topic}</td>
                        <td className="center">
                          <span className="percentage-badge badge-blue">{row.l1}</span>
                        </td>
                        <td className="center">
                          <span className="percentage-badge badge-orange">{row.l2}</span>
                        </td>
                        <td className="center">
                          <span className="percentage-badge badge-green">{row.l3}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="table-footer">
                <p className="table-footer-text">
                  <strong>Training Modes:</strong> LMS, Visual Learning, Simulation, Workshops, Hands-on
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Main Image Section 3 */}
        <section className="section section-white">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">
                Our Visual TNI Model
              </h2>
              <p className="section-subtitle">3D framework: Designation × Employees × Skill Topics</p>
            </div>
            <div className="image-wrapper">
              <img
                src={tni3}
                alt="Visual TNI model"
                loading="lazy"
              />
            </div>
            <div className="model-info-box">
              <p className="model-info-title">
                <strong>Training is planned using a 3D framework:</strong> X-Axis (Designation: L1, L2, L3) • Y-Axis (Number of employees) • Z-Axis (Skill topics: 5S, Lean, Root Cause, Kaizen, MP)
              </p>
              <p className="model-info-text">This ensures training is delivered only where it is needed.</p>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="section section-gray">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">
                Why Your Industry Needs TNI
              </h2>
            </div>

            <div className="benefits-grid">
              {[
                { icon: TrendingUp, title: 'Improve Productivity & Quality', color: 'blue' },
                { icon: CheckCircle, title: 'Reduce Rework and Operational Mistakes', color: 'green' },
                { icon: Users, title: 'Prepare Workforce for Future Roles', color: 'purple' },
                { icon: Target, title: 'Improve Employee Retention', color: 'orange' },
                { icon: BarChart3, title: 'Ensure ROI on Learning Investment', color: 'red' }
              ].map((benefit, i) => {
                const Icon = benefit.icon;
                const iconBg = benefit.color === 'blue' ? '#dbeafe' :
                  benefit.color === 'green' ? '#bbf7d0' :
                    benefit.color === 'purple' ? '#e9d5ff' :
                      benefit.color === 'orange' ? '#fed7aa' : '#fecaca';
                const iconColor = benefit.color === 'blue' ? '#2563eb' :
                  benefit.color === 'green' ? '#16a34a' :
                    benefit.color === 'purple' ? '#9333ea' :
                      benefit.color === 'orange' ? '#ea580c' : '#dc2626';

                return (
                  <div key={i} className="benefit-card">
                    <div className="benefit-icon" style={{ background: iconBg, color: iconColor }}>
                      <Icon size={28} />
                    </div>
                    <h3 className="benefit-title">{benefit.title}</h3>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Main Image Section 4 */}
        <section className="section section-white">
          <div className="container">
            {/* <div className="section-header">
              <h2 className="section-title">
                Industries We Serve
              </h2>
              <p className="section-subtitle">Sector-wise focus and applicability</p>
            </div> */}
            <div className="image-wrapper">
              <img
                src={tni4}
                alt="Industries we serve"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* Why SkillServe */}
        <section className="why-section">
          <div className="container">
            <div className="section-header">
              <h2 className="why-title">Why SkillServe</h2>
              <p className="why-subtitle">We don't sell courses. We build performance systems.</p>
            </div>

            <div className="features-grid">
              {features.map((f, idx) => (
                <div className="feature-item" key={idx}>
                  <div className="feature-icon">
                    <CheckCircle size={28} />
                  </div>
                  <div className="feature-text">{f}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="cta-section">
          <div className="cta-container">
            <h2 className="cta-title">Ready to convert learning into performance?</h2>
            <p className="cta-subtitle">Request a custom TNI study for your teams and get an actionable gap report.</p>

            <div className="cta-buttons">
              <button
                className="btn-cta-primary"
                onClick={() => {
                  setIsModalOpen(true);
                }}
              >
                Request TNI Assessment
              </button>
              <button
                className="btn-cta-secondary"
                onClick={() => {
                  if (typeof window !== 'undefined') window.location.href = '/allCourses/all';
                }}
              >
                View All Courses
              </button>
            </div>

            <p style={{ color: '#64748b' }}>Or email us at <a href="mailto:support@skillserve.in" style={{ color: '#1e3a8a', fontWeight: 600 }}>support@skillserve.in</a></p>
          </div>
        </section>


      </div>
      <ConsultationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        defaultQueryOption="Looking for TNI , training need identenfication"
      />
    </Layout>
  );
}
